import { ACHIEVEMENTS, FAMILY_ORDER, TIER_POINTS, type Achievement, type AchievementFamily } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { useProfile } from '../profile.js';
import type { StringKey } from '../i18n/index.js';

/**
 * The trophy case.
 *
 * Locked badges are shown, not hidden: the catalogue is the map. Anything
 * countable carries a progress bar, so "63 of 100 pairs" is visible long before
 * the badge itself is.
 */
export function TrophyCase({ onClose }: { onClose: () => void }) {
  const { t, num, settings } = useSettings();
  const { profile, earnedCodes, badgePoints, counts, progressCtx, reset } = useProfile();
  const ctx = progressCtx(settings.grade);
  const lt = profile.lifetime;

  const byFamily = (f: AchievementFamily) => ACHIEVEMENTS.filter((a) => a.family === f);

  return (
    <div className="trophy" role="dialog" aria-modal="true" aria-label={t('trophy.title')}>
      <header className="trophy-hd">
        <div>
          <h1>{t('trophy.title')}</h1>
          <p>{t('trophy.subtitle')}</p>
        </div>
        <button className="gbtn pri" onClick={onClose}>{t('btn.close')}</button>
      </header>

      <div className="trophy-body">
        <div className="trophy-stats">
          <Big label={t('trophy.earned', { a: counts.held, b: counts.total })} value={`${num(counts.held)}/${num(counts.total)}`} />
          <Big label={t('trophy.points')} value={num(badgePoints)} />
          <Big label={t('trophy.streak')} value={num(lt.dayStreak)} />
          <Big label={t('trophy.matches')} value={num(lt.totalMatches)} />
          <Big label={t('trophy.cleared')} value={num(lt.boardsCleared)} />
          <Big label={t('trophy.bestCombo')} value={`×${num(lt.bestCombo)}`} />
        </div>

        {FAMILY_ORDER.map((fam) => (
          <section className="trophy-fam" key={fam}>
            <div className="trophy-fam-hd">
              <h2>{t(`fam.${fam}` as StringKey)}</h2>
              <p>{t(`fam.${fam}.d` as StringKey)}</p>
            </div>
            <div className="badge-grid">
              {byFamily(fam).map((a) => (
                <BadgeCell
                  key={a.code}
                  ach={a}
                  held={earnedCodes.has(a.code)}
                  earnedAt={profile.earned[a.code]}
                  progress={a.progress?.(ctx)}
                />
              ))}
            </div>
          </section>
        ))}

        <button
          className="trophy-reset"
          onClick={() => { if (window.confirm(t('trophy.resetAsk'))) reset(); }}
        >
          {t('trophy.reset')}
        </button>
      </div>
    </div>
  );
}

function Big({ label, value }: { label: string; value: string }) {
  return (
    <div className="trophy-stat">
      <span className="trophy-stat-v">{value}</span>
      <span className="trophy-stat-k">{label}</span>
    </div>
  );
}

function BadgeCell({
  ach, held, earnedAt, progress,
}: {
  ach: Achievement;
  held: boolean;
  earnedAt: string | undefined;
  progress: { have: number; need: number } | undefined;
}) {
  const { t, num, settings } = useSettings();
  const pct = progress && progress.need > 0 ? Math.round((progress.have / progress.need) * 100) : 0;

  return (
    <div className={`badge${held ? ' is-held' : ''}`} data-tier={ach.tier}>
      <span className="badge-medal" aria-hidden="true">{held ? ach.icon : '🔒'}</span>
      <span className="badge-text">
        <b>{t(ach.nameKey as StringKey)}</b>
        <small>{t(ach.descKey as StringKey)}</small>
        {held ? (
          <span className="badge-foot">
            <span className="badge-tier" data-tier={ach.tier}>{t(`tier.${ach.tier}` as StringKey)}</span>
            {earnedAt && (
              <span className="badge-date">
                {t('trophy.on', { d: new Date(earnedAt).toLocaleDateString(settings.lang === 'ar' ? 'ar-EG' : 'en-GB') })}
              </span>
            )}
          </span>
        ) : progress ? (
          <span className="badge-prog">
            <span className="badge-bar"><i style={{ width: `${pct}%` }} /></span>
            <span className="badge-count">{t('trophy.progress', { a: progress.have, b: progress.need })}</span>
          </span>
        ) : (
          <span className="badge-foot">
            <span className="badge-tier" data-tier={ach.tier}>{t(`tier.${ach.tier}` as StringKey)}</span>
            <span className="badge-date">{t('ach.points', { n: TIER_POINTS[ach.tier] })}</span>
          </span>
        )}
      </span>
    </div>
  );
}
